import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SinConexionKawaii from "../SinConexionKawaii";
import SabiasQue from "../SabiasQue";

// Template162: Orsay (híbrido: mapa + Inválidos + Puente Alejandro III + Orsay). Todo desde backend.
// page.texts[0]=header, [1]=burbuja sobre mapa, [2]=intro | page.maps[0]=URL | page.sections[0..2] (imageId, title, body, sabiasQue[0]) | page.images, page.canvasImages (logo)
const Template162 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const [senaNocheImageError, setSenaNocheImageError] = useState(false);
	const { setMapFullscreen } = useMapFullscreen() || {};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator !== "undefined") {
			setIsOnline(navigator.onLine);
			setLoaded(navigator.onLine);
		}
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found) return found.imageUrl ?? found.url;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const bubbleText = page?.texts?.[1]?.content;
	const introText = page?.texts?.[2]?.content;

	const mapUrl = page?.maps?.[0];
	const logoImage = getCanvasImageUrl("logo");
	const senaNocheImage = getImageSrc("sena-noche");

	const sections = Array.isArray(page?.sections) ? page.sections : [];
	const section0 = sections[0];
	const section1 = sections[1];
	const section2 = sections[2];

	const invalidosImage = getImageSrc(section0?.imageId);
	const puenteStickerImage = getImageSrc(section1?.imageId);

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={() => setFullscreen(false)}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<iframe
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				></iframe>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			<Box
				data-block-id="map"
				sx={{
					height: 360,
					position: "relative",
					flexShrink: 0,
					backgroundColor: "#E8F4F8",
					marginTop: "-70px",
				}}
			>
				{!loaded && isOnline && mapUrl && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: `4px solid ${actualHeaderColor}`,
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								"@keyframes spin": {
									"0%": { transform: "rotate(0deg)" },
									"100%": { transform: "rotate(360deg)" },
								},
							}}
						/>
					</Box>
				)}

				{mapUrl && isOnline && (
					<Box
						component="button"
						onClick={() => setFullscreen(true)}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: actualHeaderColor,
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}

				{mapUrl && isOnline ? (
					<iframe
						title="Mapa"
						src={mapUrl}
						width="100%"
						height="100%"
						style={{ border: 0 }}
						onLoad={() => setLoaded(true)}
					></iframe>
				) : !isOnline ? (
					<SinConexionKawaii height="100%" fontFamily={fontFamily} />
				) : !mapUrl ? (
					<Box
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: "#E8F4F8",
						}}
					>
						<Box
							component="p"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
								color: "#666",
								fontSize: 13,
							}}
						>
							Mapa no configurado
						</Box>
					</Box>
				) : null}

				{senaNocheImage && !senaNocheImageError && (
					<img
						src={senaNocheImage}
						alt=""
						onError={() => setSenaNocheImageError(true)}
						style={{
							width: 90,
							position: "absolute",
							bottom: -10,
							left: 2,
							zIndex: 3,
						}}
					/>
				)}

				{bubbleText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 60,
							left: 60,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 180,
							zIndex: 5,
						}}
					>
						{bubbleText}
					</Box>
				)}
			</Box>

			<Box data-block-id="text-1" sx={{ padding: "10px 12px", position: "relative" }}>
				{introText && (
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
					>
						{introText}
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "12px",
						marginTop: "10px",
					}}
				>
					{section0 && (
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{invalidosImage && (
								<img
									src={invalidosImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: invalidosImage ? "60%" : "100%" }}>
								{section0.title && (
									<Box component="h3" sx={estiloH3}>
										{section0.title}
									</Box>
								)}
								{section0.body && (
									<Box component="p" sx={estiloP}>
										{section0.body}
									</Box>
								)}
							</Box>
							{section0.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
									<SabiasQue
										icono={section0.sabiasQue[0].icono}
										texto={section0.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{section1 && (
						<Box data-block-id="text-3" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							<Box sx={{ width: "100%" }}>
								<Box
									component="p"
									sx={{
										...estiloP,
										padding: "12px",
										backgroundColor: "#E8F4F8",
										borderRadius: "12px",
									}}
								>
									{puenteStickerImage && (
										<img
											src={puenteStickerImage}
											alt=""
											style={{
												width: isSmallScreen ? 105 : 115,
												float: "right",
												marginRight: -54,
												marginTop: -40,
												borderRadius: 12,
											}}
										/>
									)}
									{section1.title && (
										<Box component="h3" sx={estiloH3}>
											{section1.title}
										</Box>
									)}
									{section1.body}
								</Box>
							</Box>
						</Box>
					)}

					{section2 && (
						<Box
							data-block-id="text-4"
							sx={{
								backgroundColor: "#FFF5E6",
								padding: "12px",
								borderRadius: "12px",
								position: "relative",
							}}
						>
							{section2.title && (
								<Box component="h3" sx={estiloH3}>
									{section2.title}
								</Box>
							)}
							{section2.body && (
								<Box component="p" sx={estiloP}>
									{section2.body}
								</Box>
							)}
							{section2.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: -12, right: "33%" }}>
									<SabiasQue
										icono={section2.sabiasQue[0].icono}
										texto={section2.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template162.thumbnail = "/images/template-1.png";

export default Template162;
